import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import Hls from 'hls.js';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { buildSearchIndex, searchResults, buildHierarchyIndex, suggestHierarchy } from './searchApi';
import { videoPct, videoResume } from './data/progress';
import SearchBar, { takePendingUpload } from './SearchBar';
import InterpretationPanel, { PANEL_TIERS } from './InterpretationPanel';
import { PANEL } from './panelTheme';
import { usePhases, estimateSeconds } from './waitPhrases';
import { renderMathIn, mathLiveDiagnostics } from './mathliveTheme';
import LexicalResults from './LexicalResults';
import UpgradePrompt from './UpgradePrompt';

// ============================================================================
// SearchResultsPage — the committed-search view (reached on Enter, /search?q=).
//
// Modeled on LibraryPage but SELF-CONTAINED (LibraryPage is untouched), so the
// components below are trimmed local copies. The keyword results are computed
// locally and ALWAYS render — this is also the graceful fallback for when the
// semantic/Claude backend is wired: the page shows keyword rows instantly and
// the interpretation panel is purely additive and sits ABOVE them, so the
// rows stay usable while it resolves.
//
// Hover previews start UNMUTED after the first page interaction (same approach as
// the library), since the previews matter here. Cards route to /video/{id} with
// NO course param, per the search entry-point spec.
// ============================================================================

// Browsers block audio until the user has interacted with the page (a hover
// doesn't count). We flip this flag on the first real gesture so previews from
// then on start UNMUTED. Reaching this page requires a click, so it unlocks
// effectively immediately. (Harmless if the library installs its own copy too.)
let AUDIO_UNLOCKED = false;
if (typeof window !== 'undefined') {
  const unlock = () => {
    AUDIO_UNLOCKED = true;
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
    window.removeEventListener('touchstart', unlock, true);
  };
  window.addEventListener('pointerdown', unlock, true);
  window.addEventListener('keydown', unlock, true);
  window.addEventListener('touchstart', unlock, true);
}

// --- HLS hover preview (mounts only while hovered) --------------------------
function HoverPreview({ src, objectPosition = 'center', highQuality = false, resumeFrom = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v || !src) return undefined;
    const seekOnce = () => {
      if (resumeFrom > 0 && Number.isFinite(v.duration) && resumeFrom < v.duration) {
        try { v.currentTime = resumeFrom; } catch (e) { /* noop */ }
      }
    };
    v.addEventListener('loadedmetadata', seekOnce, { once: true });
    let hls;
    if (/\.m3u8(\?|$)/i.test(src) && Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, capLevelToPlayerSize: !highQuality, abrEwmaDefaultEstimate: 8000000, startPosition: resumeFrom > 0 ? resumeFrom : -1 });
      hls.attachMedia(v);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(src));
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (highQuality && hls.levels && hls.levels.length) {
          // Pin to the highest-bitrate variant so the hero starts sharp (no low-res ramp).
          let top = 0, max = -1;
          hls.levels.forEach((lvl, i) => { if (lvl.bitrate > max) { max = lvl.bitrate; top = i; } });
          hls.autoLevelCapping = -1;
          hls.startLevel = top;
          hls.currentLevel = top;
        }
      });
    } else {
      v.src = src;
    }
    // Start unmuted once the page has been interacted with; fall back to muted
    // playback if the browser still rejects it.
    v.volume = 1;
    v.muted = !AUDIO_UNLOCKED;
    const p = v.play();
    if (p && p.catch) p.catch((err) => {
      if (err && err.name === 'AbortError') return;
      v.muted = true;
      const pm = v.play();
      if (pm && pm.catch) pm.catch(() => {});
    });
    return () => { v.removeEventListener('loadedmetadata', seekOnce); try { v.pause(); } catch (e) { /* noop */ } if (hls) hls.destroy(); };
  }, [src, highQuality, resumeFrom]);
  return (
    <video ref={ref} loop playsInline
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition }} />
  );
}

// --- Thumbnail (poster + hover preview + play affordance + lock) ------------
function ResultThumb({ video, colors, isHovered, isMobile }) {
  const { progressByVideo } = useAuth();
  const pct = videoPct(progressByVideo, video.videoId);
  return (
    <div style={{
      position: 'relative', aspectRatio: '16/9', borderRadius: isMobile ? '8px' : '10px', overflow: 'hidden',
      boxShadow: isHovered ? `0 20px 40px -12px rgba(0,0,0,0.4), 0 0 0 1px ${colors.accent}30` : '0 8px 20px -8px rgba(0,0,0,0.3)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      transform: isHovered && !isMobile ? 'translateY(-4px)' : 'translateY(0)',
    }}>
      {video.thumbnailUrl
        ? <img src={video.thumbnailUrl} alt="" loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        : <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,#667eea,#764ba2)' }} />}

      <div style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)', opacity: (isHovered || isMobile) && !video.locked ? 1 : 0, transition: 'opacity 0.3s ease',
      }}>
        <div style={{
          width: isMobile ? '44px' : '56px', height: isMobile ? '44px' : '56px', borderRadius: '50%',
          background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
        }}>
          <svg width={isMobile ? 18 : 22} height={isMobile ? 18 : 22} viewBox="0 0 24 24" fill="#000" style={{ marginLeft: '2px' }}><path d="M8 5v14l11-7z" /></svg>
        </div>
      </div>

      {isHovered && !video.locked && video.previewUrl && <HoverPreview src={video.previewUrl} resumeFrom={videoResume(progressByVideo, video.videoId)} />}

      {pct > 0 && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: isMobile ? '3px' : '4px', background: 'rgba(0,0,0,0.5)' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: colors.accent, boxShadow: `0 0 10px ${colors.accent}`, transition: 'width 0.3s ease' }} />
        </div>
      )}

      {video.locked && (
        <div style={{
          position: 'absolute', top: isMobile ? '8px' : '10px', left: isMobile ? '8px' : '10px',
          display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderRadius: '4px',
          background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: isMobile ? '9px' : '10px',
          fontFamily: '"DM Sans", sans-serif', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
          Locked
        </div>
      )}
    </div>
  );
}

// --- Card -------------------------------------------------------------------
function ResultCard({ video, colors, isHovered, onHover, onLeave, size, isMobile, isTablet, onNavigate }) {
  const width = isMobile ? (size === 'large' ? '280px' : '200px')
    : isTablet ? (size === 'large' ? '320px' : '240px')
      : (size === 'large' ? '400px' : '300px');
  const extra = (video.matchType === 'description' || video.matchType === 'tag') && video.snippet
    ? (video.snippet.length > 64 ? video.snippet.slice(0, 64) + '…' : video.snippet) : null;

  return (
    <div onMouseEnter={onHover} onMouseLeave={onLeave} onClick={() => onNavigate(video)}
      style={{ flex: '0 0 auto', width, cursor: 'pointer' }}>
      <ResultThumb video={video} colors={colors} isHovered={isHovered} isMobile={isMobile} />
      <div style={{ marginTop: isMobile ? '10px' : '14px', paddingLeft: '2px' }}>
        <h3 style={{
          fontSize: isMobile ? '14px' : size === 'large' ? '17px' : '15px', fontWeight: 500, color: colors.text,
          fontFamily: '"DM Sans", sans-serif', margin: 0, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{video.title}</h3>
        {extra && (
          <p style={{ fontSize: isMobile ? '12px' : '13px', color: colors.accent, fontFamily: '"DM Sans", sans-serif', margin: '4px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{extra}</p>
        )}
      </div>
    </div>
  );
}

// --- Row (horizontal scroller) ----------------------------------------------
function ResultRow({ title, videos, colors, isLightTheme, size, isMobile, isTablet, onNavigate }) {
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const rowRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const updateArrows = () => {
    const el = rowRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };
  const scroll = (dir) => {
    if (rowRef.current) rowRef.current.scrollBy({ left: dir === 'left' ? -rowRef.current.clientWidth * 0.75 : rowRef.current.clientWidth * 0.75, behavior: 'smooth' });
  };
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  const cardW = isMobile ? (size === 'large' ? 280 : 200) : isTablet ? (size === 'large' ? 320 : 240) : (size === 'large' ? 400 : 300);
  const capH = Math.round(cardW * 9 / 16);

  return (
    <section style={{ marginBottom: isMobile ? '36px' : '48px', overflow: 'hidden' }}>
      <div style={{ marginBottom: isMobile ? '16px' : '20px', paddingLeft: '4px' }}>
        <h2 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 400, color: colors.text, fontFamily: '"Cormorant Garamond", serif', margin: 0 }}>{title}</h2>
      </div>
      <div style={{ position: 'relative', margin: '0 -16px', padding: '0 16px' }}>
        {!isMobile && showLeftArrow && (
          <button onClick={() => scroll('left')} style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-70%)', width: '44px', height: '44px', borderRadius: '50%', background: isLightTheme ? '#FFFFFF' : '#1A1A1A', border: `1px solid ${isLightTheme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', cursor: 'pointer', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.text} strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
        )}
        {!isMobile && showRightArrow && (
          <button onClick={() => scroll('right')} style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-70%)', width: '44px', height: '44px', borderRadius: '50%', background: isLightTheme ? '#FFFFFF' : '#1A1A1A', border: `1px solid ${isLightTheme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', cursor: 'pointer', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.text} strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
          </button>
        )}
        <div ref={rowRef} onScroll={updateArrows} className="video-row-scroll" style={{ display: 'flex', gap: isMobile ? '12px' : '20px', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch', paddingBottom: '8px', paddingLeft: '4px', paddingRight: '4px' }}>
          {videos.map((video) => (
            <ResultCard key={video.videoId} video={video} colors={colors} size={size} isMobile={isMobile} isTablet={isTablet}
              isHovered={hoveredVideo === video.videoId} onHover={() => setHoveredVideo(video.videoId)} onLeave={() => setHoveredVideo(null)} onNavigate={onNavigate} />
          ))}
          <div style={{
            flex: '0 0 auto', width: cardW, height: capH,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '12px', padding: '0 20px',
            borderRadius: isMobile ? '8px' : '10px',
            background: isLightTheme ? 'rgba(0,0,0,0.018)' : 'rgba(255,255,255,0.022)',
            border: `1px solid ${isLightTheme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)'}`,
          }}>
            <div style={{ width: '26px', height: '1px', background: colors.accent, opacity: 0.55 }} />
            <div style={{ color: colors.muted, fontSize: isMobile ? '13px' : '14px', fontStyle: 'italic', fontFamily: '"Cormorant Garamond", serif', lineHeight: 1.45 }}>
              {videos.length === 0 ? 'No videos match your search terms' : 'No more videos match your search terms'}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Best-match hero --------------------------------------------------------
// RETIRED — see the render. Kept defined so uncommenting one line restores it.
// eslint-disable-next-line no-unused-vars
function BestMatchHero({ feature, colors, isLight, isMobile, isTablet, onNavigate }) {
  const [isHovered, setIsHovered] = useState(false);
  const { progressByVideo } = useAuth();
  if (!feature) return null;
  const heroPct = videoPct(progressByVideo, feature.videoId);
  const go = () => onNavigate(feature);
  return (
    <section onClick={go} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
      style={{ position: 'relative', height: isMobile ? '380px' : isTablet ? '420px' : '480px', marginBottom: isMobile ? '32px' : '48px', borderRadius: isMobile ? '14px' : '18px', overflow: 'hidden', cursor: feature.locked ? 'default' : 'pointer', border: `1px solid ${isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.08)'}`, boxShadow: isHovered ? `0 28px 64px -22px ${colors.accentGlow}` : (isLight ? '0 14px 36px -18px rgba(0,0,0,0.20)' : '0 14px 36px -18px rgba(0,0,0,0.55)'), transition: 'box-shadow 0.4s ease' }}>
      <div style={{ position: 'absolute', inset: 0, zIndex: 0, transform: isHovered && !isMobile ? 'scale(1.05)' : 'scale(1)', transition: 'transform 0.7s cubic-bezier(0.4,0,0.2,1)' }}>
        {feature.thumbnailUrl && <img src={feature.thumbnailUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />}
        {isHovered && !feature.locked && feature.previewUrl && <HoverPreview src={feature.previewUrl} objectPosition="top" highQuality resumeFrom={videoResume(progressByVideo, feature.videoId)} />}
      </div>
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 36%, rgba(0,0,0,0.22) 68%, rgba(0,0,0,0.32) 100%), linear-gradient(90deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 55%, transparent 100%)' }} />
      <div style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: isMobile ? '24px' : isTablet ? '40px 44px' : '52px 60px', maxWidth: isMobile ? '100%' : '680px' }}>
        <span style={{ alignSelf: 'flex-start', padding: isMobile ? '4px 10px' : '6px 14px', background: colors.accent, borderRadius: '4px', fontSize: isMobile ? '10px' : '11px', fontWeight: 700, color: '#1A1A1A', textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: '"DM Sans", sans-serif', marginBottom: isMobile ? '12px' : '16px' }}>Best match</span>
        <h1 style={{ fontSize: isMobile ? '32px' : isTablet ? '44px' : '56px', fontWeight: 400, color: '#FFFFFF', fontFamily: '"Cormorant Garamond", serif', lineHeight: 1.05, letterSpacing: '-0.01em', margin: 0, textShadow: '0 2px 16px rgba(0,0,0,0.5)' }}>{feature.title}</h1>
        {!feature.locked && (
          <div style={{ display: 'flex', gap: '12px', marginTop: isMobile ? '20px' : '26px' }}>
            <button onClick={(e) => { e.stopPropagation(); go(); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: isMobile ? '13px 22px' : '15px 30px', background: colors.accent, border: 'none', borderRadius: '10px', fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: '#1A1A1A', fontFamily: '"DM Sans", sans-serif', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>
              <svg width={isMobile ? 16 : 18} height={isMobile ? 16 : 18} viewBox="0 0 24 24" fill="#1A1A1A"><path d="M8 5v14l11-7z" /></svg>
              <span>Watch</span>
            </button>
          </div>
        )}
      </div>
      {heroPct > 0 && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2, height: isMobile ? '4px' : '5px', background: 'rgba(0,0,0,0.5)' }}>
          <div style={{ height: '100%', width: `${heroPct}%`, background: colors.accent, boxShadow: `0 0 12px ${colors.accent}`, transition: 'width 0.3s ease' }} />
        </div>
      )}
    </section>
  );
}

// --- Search Description block (superseded by the interpretation panel) ------
// Kept as the fallback if the panel is removed, and because it is what free   
// want the old placeholder copy back; nothing calls it today.
// eslint-disable-next-line no-unused-vars
function SearchDescription({ query, description, colors, isLight, isMobile }) {
  return (
    <section style={{ marginBottom: isMobile ? '36px' : '48px' }}>
      <h2 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 400, color: colors.text, fontFamily: '"Cormorant Garamond", serif', margin: '0 0 14px', paddingLeft: '4px' }}>Search Description</h2>
      <div style={{ padding: isMobile ? '18px' : '22px 26px', borderRadius: '14px', background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)', border: `1px solid ${isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.08)'}` }}>
        {description ? (
          <p style={{ margin: 0, color: colors.text, fontSize: isMobile ? '14px' : '15px', lineHeight: 1.7, fontFamily: '"DM Sans", sans-serif' }}>{description}</p>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className="sr-pulse" style={{ width: '9px', height: '9px', borderRadius: '50%', background: colors.accent, flexShrink: 0 }} />
            <p style={{ margin: 0, color: colors.muted, fontSize: isMobile ? '14px' : '15px', lineHeight: 1.6, fontStyle: 'italic', fontFamily: '"DM Sans", sans-serif' }}>
              An AI-written answer for “{query}” will appear here once semantic search is connected.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}


// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  THE INTERPRETATION PANEL                                                ║
// ║                                                                          ║
// ║  WHO SEES IT: PANEL_TIERS in InterpretationPanel.jsx — pro_plus only.    ║
// ║               The orrery and the error box follow the same list via      ║
// ║               `showPanel` in the page body. Free and pro see the lexical ║
// ║               rows only. The requests still run for every tier.          ║
// ║                                                                          ║
// ║  This page owns almost none of it. It reads the upload handoff, runs     ║
// ║  /search/image when there is a file, shows the orrery while that runs,   ║
// ║  and hands the problems to the panel. The panel owns the log, drafts,    ║
// ║  derive, the generation counter, and the /search/ask call itself.        ║
// ║                                                                          ║
// ║  REMOVE: delete the <InterpretationPanel/> line in the render, the       ║
// ║          1.6 block in the page body, and the import.                     ║
// ╚══════════════════════════════════════════════════════════════════════════╝

// ── THREE PATHWAYS ───────────────────────────────────────────────────────
// free      lexical rows only on screen. /search/text and /search/ask still
//           fire in the background; their results are not shown.
// pro       the same as free FOR NOW — the panel is hidden until its features
//           are finished (PANEL_TIERS). When it returns: the card and resubmit,
//           no answer section (ANSWER_TIERS).
// pro_plus  the panel, the card, resubmit, and the answer.
//
// ENRICHMENT IS OFF FOR THIS BUILD. Every tier gets plain lexical ordering,
// the same as free. `ENRICH_TIERS`, `enrichTerms` and the merge that reordered
// the rows are gone, along with the second /search/ask call that fed them.
// See "Enrichment — per-problem, and switched OFF until then" in the punchlist.

// ════════════════════════════════════════════════════════════════════════════
// TEMPORARY — punchlist 1.1, prove MathLive renders before building on it.
// Rendered ONLY in the empty-query branch (/search with no ?q=), so it cannot
// touch the results path. DELETE THIS COMPONENT and its one call site once the
// 1.1 checklist is ticked.
//
// Owns NO configuration. The delimiters, palette, click highlight and keyboard
// inset are all set up by useMathLive() in AppLayout and apply app-wide — so
// what this renders is what a real card will render, and deleting it changes
// nothing about how MathLive behaves.
// ════════════════════════════════════════════════════════════════════════════
function MathProof({ colors, isLight }) {
  const mixedRef = useRef(null);
  const fieldRef = useRef(null);
  const [diag, setDiag] = useState(null);
  const [latex, setLatex] = useState('');

  useEffect(() => {
    // The mixed line. Prose stays prose; only the $…$ spans are typeset.
    // renderMathIn supplies the delimiter options — without them MathLive
    // ignores single dollars and prints them literally.
    const typeset = renderMathIn(
      mixedRef.current,
      'Find the inverse of $f(x) = 3x + 11$ and state its domain, giving it as $\\frac{x - 6}{2}$.'
    );

    setDiag({ ...mathLiveDiagnostics(), typeset });

    const mf = fieldRef.current;
    if (!mf) return undefined;
    mf.value = '\\frac{x - 6}{2}';
    setLatex(mf.value);
    const onInput = () => setLatex(mf.value);
    mf.addEventListener('input', onInput);
    return () => mf.removeEventListener('input', onInput);
  }, []);

  const line = isLight ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.14)';
  const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px' };
  const box = {
    border: '1px solid ' + line,
    borderRadius: '10px',
    padding: '16px 18px',
    marginBottom: '16px',
    textAlign: 'left',
    background: colors.cardBg,
  };
  const cap = { ...mono, color: colors.muted, margin: '0 0 10px' };
  const btn = {
    ...mono,
    padding: '7px 14px',
    borderRadius: '8px',
    cursor: 'pointer',
    border: '1px solid ' + line,
    background: 'transparent',
    color: colors.text,
  };

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', textAlign: 'left' }}>
      <p style={{ ...mono, color: colors.muted, margin: '0 0 12px' }}>
        punchlist 1.1 — temporary
      </p>

      <div style={box}>
        {!diag ? (
          <p style={{ ...mono, color: colors.muted, margin: 0 }}>checking…</p>
        ) : (
          <div style={{ ...mono, color: colors.text, lineHeight: 1.8 }}>
            <div>version: {String(diag.version)}</div>
            <div>math-field registered: {diag.registered ? 'yes' : 'NO'}</div>
            <div>renderMathInElement: {diag.canRender ? 'yes' : 'MISSING — SSR build'}</div>
            <div>typeset ran: {diag.typeset ? 'yes' : 'NO'}</div>
            <div>mathVirtualKeyboard: {diag.keyboard ? 'yes' : 'no'}</div>
          </div>
        )}
      </div>

      <div style={box}>
        <p style={cap}>editable — click into the fraction. only the caret should move.</p>
        <math-field ref={fieldRef} style={{ fontSize: '24px', width: '100%' }} />
        <p style={{ ...mono, color: colors.muted, margin: '12px 0 0', wordBreak: 'break-all' }}>
          {latex || '(empty)'}
        </p>
      </div>

      <div style={box}>
        <p style={cap}>renderMathInElement — words as words, formulas typeset</p>
        <div ref={mixedRef} style={{ fontSize: '18px', lineHeight: 1.8, color: colors.text }} />
      </div>

      <div style={box}>
        <p style={cap}>keyboard — tap on a phone. the field should stay visible.</p>
        <math-field style={{ fontSize: '22px', width: '100%' }} />
        <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
          <button type="button" style={btn} onClick={() => window.mathVirtualKeyboard?.show()}>
            show
          </button>
          <button type="button" style={btn} onClick={() => window.mathVirtualKeyboard?.hide()}>
            hide
          </button>
        </div>
      </div>
    </div>
  );
}

// Kill switch, independent of tier. False disables the server call for everyone
// and every tier falls back to plain lexical search.
const ASK_ENDPOINT = '/search/ask';
// THE SAME ANSWER, FOR A TREE THE STUDENT HAS EDITED. An exact copy of /ask
// today — see the fork note above /ask in routes/search.js. Forked because a
// resubmit and a first ask are EXPECTED to diverge, and a student asking their
// first question should never be affected by a change made for editing.
const RESUBMIT_ENDPOINT = '/search/resubmit';
const IMAGE_ENDPOINT = '/search/image';
// The typed sibling of /search/image. Turns a question into a tree whose
// elements carry part labels, so both paths reach /search/ask in one shape.
const TEXT_ENDPOINT = '/search/text';

// The three answer actions. The path is built from the action id, so `ACTIONS`
// in ProblemCard and the routes stay in step without a lookup table.
const EXPLAIN_ENDPOINT = (action) => `/search/explain/${action}`;


// ════════════════════════════════════════════════════════════════════════════
// THE ORRERY — punchlist 1.5
// ════════════════════════════════════════════════════════════════════════════
//
// WHAT WAS HERE: `useClassification` and `AnswerPanel`, which fetched a flat
// classification from /search/ask and rendered it. Both are gone. The panel
// makes that call now, and its answer carries the search terms — keeping this
// would mean TWO /search/ask calls per page.
//
// ENRICHMENT IS OFF for this build. Every tier gets plain lexical ordering, the
// same as free. Turning it back on is its own section at the end of the
// punchlist; it is a design question, not plumbing.
//
// The orrery is the image path's FIRST STATE: it shows ALONE while
// /search/image is in flight, because there is genuinely nothing to render yet.
// The typed path never shows it — the question is already on screen.
//
// It sits on PANEL, not on the tier palette. This IS the panel, before it has
// anything to hold.

// Counts down from the estimate. A COUNTDOWN, NOT A PROGRESS BAR: we cannot
// know how far along transcription is, only how long it usually takes, and a
// bar that claims 60% would be inventing that.
//
// Stops at "a few more seconds" rather than hitting zero and going negative —
// the estimate is a proxy from file size (see waitPhrases.js) and will
// sometimes be short.
function useCountdown(active, seconds) {
  const [left, setLeft] = React.useState(seconds);

  React.useEffect(() => {
    if (!active) { setLeft(seconds); return undefined; }
    setLeft(seconds);
    const id = setInterval(() => setLeft((n) => (n > 1 ? n - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [active, seconds]);

  return left;
}

function Orrery({ phrase, seconds, isMobile }) {
  const gild = PANEL.accent;

  return (
    // Fluid, like the card. The container below sets the width once.
    <div style={{ background: PANEL.bg, borderRadius: 12, padding: '1rem', marginBottom: 16 }}>
      <div style={{
        background: PANEL.surface,
        border: `0.5px solid ${PANEL.border}`,
        borderRadius: 12,
        padding: isMobile ? '1.2rem 1rem' : '1.4rem 1.25rem',
        textAlign: 'center',
      }}>
        <svg width="46" height="46" viewBox="0 0 46 46" className="sa-orrery" aria-hidden="true">
          <circle cx="23" cy="23" r="19" fill="none" stroke={PANEL.borderStrong} strokeWidth="0.5" />
          <circle cx="23" cy="23" r="12" fill="none" stroke={PANEL.border} strokeWidth="0.5" />
          <path d="M23 4 A19 19 0 0 1 40 17" fill="none" stroke={gild} strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="40" cy="17" r="2" fill={gild} />
        </svg>

        {/* key={phrase} remounts the line, which is what makes the fade re-run
            on every change rather than only the first. */}
        <p key={phrase} className="sa-phase" style={{ fontSize: 14, margin: '14px 0 5px',
          color: PANEL.text, fontFamily: PANEL.sans }}>
          {phrase}
        </p>
        <p style={{ fontSize: 12.5, margin: '0 0 14px', color: PANEL.muted, fontFamily: PANEL.sans }}>
          {seconds > 0 ? `about ${seconds} seconds left` : 'a few more seconds'}
        </p>

        <div style={{ maxWidth: 230, margin: '0 auto' }}>
          <div style={{ height: 3, background: PANEL.sunken, borderRadius: 2, overflow: 'hidden' }}>
            <div className="sa-sweep" style={{ width: '32%', height: '100%',
              background: PANEL.accent, borderRadius: 2 }} />
          </div>
        </div>
      </div>
    </div>
  );
}


const ANSWER_STYLES = `
  /* The orrery, lifted from LandingPageObs: a gilded arc sweeping a faint ring.
     Two animations at different periods, so the arc grows and shrinks while the
     whole thing turns — the pattern never repeats exactly, which is what stops
     it reading as a spinner. */
  @keyframes saSpin { to { transform: rotate(360deg); } }
  @keyframes saArc {
    0%   { stroke-dashoffset: 352; }
    50%  { stroke-dashoffset: 70; }
    100% { stroke-dashoffset: 352; }
  }
  /* CENTRE, not 0 0. The old orrery used a -100..100 viewBox whose origin WAS
     the centre; this one is 0 0 46 46, so the origin is the top-left corner and
     the whole mark would orbit off its own axis. */
  .sa-orrery { transform-origin: 50% 50%; animation: saSpin 4s linear infinite; }
  .sa-arc    { animation: saArc 2s ease-in-out infinite; }

  /* The band under the orrery. Indeterminate on purpose: we cannot know how far
     transcription has got, and a bar claiming 60% would be inventing it. */
  @keyframes saSweep { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
  .sa-sweep { animation: saSweep 2s ease-in-out infinite; }

  /* The phrase crossfades in place. It must not slide down the page or the eye
     is pulled away to re-read something it has already read. */
  @keyframes saPhraseIn { from { opacity: 0; } to { opacity: 1; } }
  .sa-phase { animation: saPhraseIn 0.5s ease both; }

  /* saInk and saRise lived here for AnswerPanel's reveal. Both went with it —
     the card fades its own phrase and the panel has no pane to rise. */

  /* A mark that turns forever is exactly what this setting exists to stop. */
  @media (prefers-reduced-motion: reduce) {
    .sa-orrery, .sa-arc, .sa-phase { animation: none; }
    .sa-sweep { animation: none; width: 40%; }
  }
`;

// ╚═══════════════ END SEARCH-SERVER ANSWER BLOCK ═══════════════╝

// ============================================================================
export default function SearchResultsPage({ isMobile = false, isTablet = false, isDesktop = true, sidebarOpen = true }) {
  const { colors, isLight } = useTheme();
  const { profile, catalog, catalogLoading, api } = useAuth();
  const currentTier = profile?.currentTier || 'free';
  const isLightTheme = isLight;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const query = (searchParams.get('q') || '').trim();

  // No course param — search carries no course context.
  // Opening a topic from search. A locked topic opens the upgrade prompt; an open
  // one plays, carrying its course so the video page shows course context and
  // "Up next". Result cards pass their own item (courseId = the first course the
  // topic appears in); lessons in an expanded section pass that section's course.
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const openTopic = (item, courseId) => {
    if (!item) return;
    if (item.locked) { setUpgradeOpen(true); return; }
    navigate('/video/' + item.videoId + (courseId ? '?course=' + courseId : ''));
  };
  const onNavigate = (item) => openTopic(item, item && item.courseId);

  // profile is the source of truth for tier; currentTier on the context is a
  // compatibility scaffold that AuthContext.js says not to build against.
  // profile is the source of truth for tier; currentTier on the context is a
  // compatibility scaffold that AuthContext.js says not to build against.
  const tier = profile?.currentTier || 'free';

  // WHO SEES THE PANEL'S BOXES. The panel hides itself on tier; the orrery and
  // the error box sit beside it in this page, so they read the SAME list here.
  // One list, three boxes — widen PANEL_TIERS and all three come back together.
  //
  // VISUAL ONLY. The requests behind them still run for every tier.
  const showPanel = PANEL_TIERS.includes(tier);

  // ══════════════════════════════════════════════════════════════════════════
  // 1.6 — FIRE THE CALLS
  // ══════════════════════════════════════════════════════════════════════════
  //
  // Two entry paths, ONE destination: a set of problems handed to the panel.
  //
  //   TYPED   ?q= is present and there is no file. The query becomes a
  //           one-problem tree labelled `question` and goes straight to the
  //           panel. No orrery — the question is already on screen, so there
  //           is nothing to wait at blankly.
  //
  //   IMAGE   location.state carries a marker and SearchBar holds the File.
  //           /search/image runs first and the
  //           orrery shows ALONE while it does, because there is genuinely
  //           nothing to render. Its tree then goes to the panel, which fires
  //           /search/ask.
  //
  // The panel owns everything downstream: the log, the drafts, derive, the
  // generation counter, and the ask itself. This page only starts things.

  // KEYED ON `location.key`, NOT CAPTURED IN A REF.
  //
  // A ref read on first mount was WRONG and broke the commonest case: dropping
  // a file into the search bar WHILE ALREADY ON /search. Navigating from
  // /search to /search does not remount this component, so the ref kept its
  // first value — null — and the file was silently dropped. The URL changed and
  // nothing else happened.
  //
  // `location.key` is unique per navigation entry, so the effect below fires
  // once per navigation and reads the state that arrived with it. A File still
  // cannot survive a refresh, which is why ?q= remains the lexical fallback.
  const navKey = location.key;

  // BOTH SCOPED TO THE NAVIGATION, and read back through it.
  //
  // Page state survives a navigation, so after keying the panel on `navKey` a
  // freshly mounted panel would see the PREVIOUS question's problems for a
  // render and fire them. Stamping the navigation onto the state and comparing
  // at READ time discards the stale value immediately — no second effect, no
  // frame where the old question is live.
  const [phase, setPhase] = useState({ key: null, status: 'idle', problems: null, error: null });
  const live = phase.key === navKey ? phase : { status: 'idle', problems: null, error: null };
  const imageStatus = live.status;
  const askProblems = live.problems;
  const uploadError = live.error;
  // How long to tell the student this will take. A PROXY from file size — the
  // thing that actually predicts it is the element count, which is what
  // transcription returns. See waitPhrases.js.
  const [estimate, setEstimate] = useState(45);

  // A typed question, in the contract's bare-question shape. `question` is
  // RESERVED — a worksheet problem can never be called this — and instructions
  // are always empty, because a typed question's instruction is inside its own
  // text.
  // A typed question, in the contract's bare-question shape. `question` is
  // RESERVED — a worksheet problem can never be called this — and instructions
  // are always empty, because a typed question's instruction is inside its own
  // text.
  const typedProblems = useMemo(() => (query ? [{
    label: 'question',
    page: 1,
    instructions: [],
    elements_array: [{ kind: 'text', origin: 'printed', struck: false, content: query }],
  }] : null), [query]);

  useEffect(() => {
    // The marker is in location.state; the FILE is in a module variable in
    // SearchBar. See the note there — a multi-megabyte File in history state is
    // what was breaking image uploads while small PDFs survived.
    //
    // Read-once. This effect fires once per navigation, and a second read would
    // be a second submission of the same page.
    const marked = !!(location.state && location.state.upload);
    const handoff = marked ? takePendingUpload() : null;

    // ALWAYS LOGS. A silent decision here cost a long debugging session: when
    // the image branch is not taken, NOTHING downstream runs, so every log
    // inside it is unreachable and the failure looks like no failure at all.
    //
    // Reading this line: `marked` false means SearchBar never navigated with an
    // upload marker. `marked` true with `file` null means the marker arrived but
    // the module handoff was empty — a second read, or a remount between the
    // two.
    console.info('[search] mount', {
      navKey,
      marked,
      file: handoff && handoff.file
        ? { name: handoff.file.name, type: handoff.file.type, bytes: handoff.file.size }
        : null,
      query: query || null,
    });

    // ── NO FILE: THE TYPED PATH ─────────────────────────────────────────────
    //
    // This used to hand `typedProblems` straight to the panel — one element
    // holding the whole question, built here. `a) find f'(x) b) evaluate at
    // x = 2` was therefore ONE problem with no parts, and the answer covered
    // only what the classifier chose to notice.
    //
    // It now goes to /search/text, which splits it, exactly as a photo goes to
    // /search/image. `typedProblems` remains as the error fallback below.
    if (!handoff || !handoff.file) {
      let cancelledText = false;
      setPhase({ key: navKey, status: 'loading', problems: null, error: null });

      (async () => {
        try {
          console.info('[search] POST', TEXT_ENDPOINT, query);
          const res = await api.fetch(TEXT_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: query }),
          });

          if (!res.ok) {
            const raw = await res.text();
            let message = null;
            try {
              const body = JSON.parse(raw);
              const fromServer = body && body.error && body.error.message;
              if (typeof fromServer === 'string' && fromServer.trim()) message = fromServer;
            } catch (_) {
              // Not JSON — something in front of the app answered.
            }
            console.error('[search] text rejected', { status: res.status, raw: raw.slice(0, 400) });
            throw new Error(message || `That question could not be read (HTTP ${res.status}).`);
          }

          const data = await res.json();
          const problems = (data && data.tree && data.tree.problems) || [];
          if (cancelledText) return;
          setPhase({ key: navKey, status: 'idle', problems, error: null });
        } catch (err) {
          if (cancelledText) return;
          console.error('[search] text failed', err);
          // THE UNSPLIT QUESTION, AND AN ERROR SAYING SO. Not a silent
          // fallback: a student must never get a confident answer to half a
          // question with nothing on screen admitting it.
          setPhase({
            key: navKey, status: 'idle',
            problems: typedProblems,
            error: err.message || 'That question could not be read.',
          });
        }
      })();

      return () => { cancelledText = true; };
    }

    let cancelled = false;
    setEstimate(estimateSeconds(handoff.file));
    setPhase({ key: navKey, status: 'loading', problems: null, error: null });

    (async () => {
      try {
        const form = new FormData();
        // FIELD NAME IS `photo`, NOT `file`. The route is
        // upload.single('photo') — multer rejects any other field name with a
        // 400 that says nothing useful.
        form.append('photo', handoff.file);
        if (handoff.text) form.append('text', handoff.text);

        // api.fetch leaves FormData alone — it must not set Content-Type, or
        // the multipart boundary is lost.
        console.info('[search] POST', IMAGE_ENDPOINT, handoff.file.name, handoff.file.size);
        const res = await api.fetch(IMAGE_ENDPOINT, { method: 'POST', body: form });
        console.info('[search] response', res.status, res.headers.get('content-type'));

        // THE SERVER'S MESSAGE WHEN THERE IS ONE, THE STATUS WHEN THERE IS NOT.
        //
        // A generic line here hid a real failure for days. The app's own errors
        // come back as `{ error: { message } }` — see the server's
        // middleware/errorHandler.js — but a failure BEFORE the app is reached
        // does not: a reverse proxy answers with HTML, `res.json()` throws, and
        // every distinct cause collapsed into one useless sentence.
        //
        // 413 is called out by name because it is the likeliest one and the app
        // never sends it for a file under its own 12MB cap. nginx's
        // `client_max_body_size` DEFAULTS TO 1MB, which is under a phone
        // screenshot and far under a camera photo — while a PDF worksheet slips
        // beneath it. That is exactly the pattern of a PDF working and every
        // image failing.
        if (!res.ok) {
          const raw = await res.text();

          let message = null;
          try {
            const body = JSON.parse(raw);
            const fromServer = body && body.error && body.error.message;
            if (typeof fromServer === 'string' && fromServer.trim()) message = fromServer;
          } catch (_) {
            // Not JSON. Something in front of the app answered — a proxy, a
            // gateway, a load balancer. The status is the only real signal.
          }

          if (!message) {
            message = res.status === 413
              ? 'That file is too large to upload. The server is refusing it before it reaches us — if it is under 12MB this is a proxy limit, not the app.'
              : `That page could not be read (HTTP ${res.status}).`;
          }

          // The raw body in the console, always. It is the difference between
          // "the app said no" and "something in front of the app said no", and
          // it costs nothing.
          console.error('[search] image rejected', {
            status: res.status,
            statusText: res.statusText,
            contentType: res.headers.get('content-type'),
            body: raw.slice(0, 500),
          });

          const err = new Error(message);
          err.userMessage = message;
          throw err;
        }

        const data = await res.json();
        if (cancelled) return;

        const problems = (data && data.tree && data.tree.problems) || [];

        console.info('[search] transcribed', {
          problems: problems.length,
          counts: data && data.counts,
          ms: data && data.ms,
        });

        // A 200 WITH NO PROBLEMS IS A FAILURE, and it used to be a SILENT one.
        //
        // `problems.length ? problems : typedProblems` fell back to the typed
        // question — which on an upload is null, because there is no ?q=. So the
        // panel received nothing, rendered nothing, and threw nothing: the
        // orrery vanished and the page was simply empty, with not one line in
        // the console.
        //
        // It reads as a crash and it is not one. The page was read; nothing on
        // it was recognised as a problem.
        if (!problems.length) {
          setPhase({
            key: navKey,
            status: 'idle',
            problems: typedProblems,
            // NO LONGER BLAMES THE PHOTO. The server now emits a `work`
            // problem for anything it read that carried no problem number, so
            // an empty tree means it genuinely read nothing at all — a failed
            // transcription, not a bad crop.
            error: typedProblems
              ? null
              : 'Nothing could be read from that page. If it looked clear, the transcriber may be down — try again in a moment.',
          });
          return;
        }

        setPhase({ key: navKey, status: 'idle', error: null, problems });
      } catch (err) {
        if (cancelled) return;
        console.error('[search] image failed', err);
        // Degrade to the lexical results rather than to a spinner that never
        // resolves. The student still gets a page — and now also gets told why
        // the upload did not work.
        setPhase({
          key: navKey,
          status: 'error',
          problems: null,
          error: err.userMessage || 'That page could not be read. Try a photo or a screenshot.',
        });
      }
    })();

    return () => { cancelled = true; };
    // location.state is read INSIDE, deliberately: including it in the deps
    // would refire on any re-render that produced an equal-but-new state
    // object. navKey changes exactly once per navigation, which is the event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navKey, typedProblems]);

  // The panel's only link to the network. It owns the log and the generation
  // counter; this just posts and returns the tree.
  const sendTree = useCallback(async (body, tag) => {
    // THE TAG CHOOSES THE ROUTE. The panel says which kind of send this is; the
    // URLs live here.
    //
    // `tag !== 'original'` is the SAME TEST the panel already uses to count
    // resubmit rounds, so `original` has one meaning in this codebase rather
    // than two. It also means a tag added later lands on the edit path, which
    // is where a new tag would come from.
    const resubmit = tag !== 'original';
    const endpoint = resubmit ? RESUBMIT_ENDPOINT : ASK_ENDPOINT;

    const res = await api.fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`${resubmit ? 'resubmit' : 'ask'} ${res.status}`);
    const data = await res.json();
    return data && data.tree;
  }, [api]);

  /**
   * One of the three answer actions.
   *
   * All three send the same payload: the problem as it was last SENT, and the
   * answer that came back for it. `Problem` and `Answer`, capitalised.
   */
  const sendExplain = useCallback(async (action, Problem, Answer) => {
    const res = await api.fetch(EXPLAIN_ENDPOINT(action), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ Problem, Answer }),
    });
    if (!res.ok) throw new Error(`explain/${action} ${res.status}`);
    return res.json();
  }, [api]);

  const transcribing = imageStatus === 'loading';
  const { phrase } = usePhases(transcribing);
  const secondsLeft = useCountdown(transcribing, estimate);

  // ── LEXICAL RESULTS ─────────────────────────────────────────────────────
  // Plain, for every tier. Enrichment is OFF for this build — the merge that
  // used to reorder these rows from the model's search_terms is gone, along
  // with the second /search/ask call that fed it. See the punchlist section.
  const index = useMemo(() => buildSearchIndex(catalog), [catalog]);
  const data = useMemo(
    () => searchResults(query, index, { currentTier }),
    [query, index, currentTier]
  );

  const populatedGroups = data.groups.filter((g) => g.items.length > 0);
  const hasResults = !!data.bestMatch || populatedGroups.length > 0;

  // Sections, chapters and courses whose own name matches — every match (the
  // results page caps rendering itself). Same index as the search bar: built once
  // per catalog inside searchApi.
  const hIndex = useMemo(() => buildHierarchyIndex(catalog), [catalog]);
  const hier = useMemo(
    () => suggestHierarchy(query, hIndex, { limit: Infinity, currentTier }),
    [query, hIndex, currentTier]
  );
  const hasHierarchy = hier.sections.length + hier.chapters.length + hier.courses.length > 0;


  const pageStyle = { padding: isMobile ? '80px 16px 32px' : isTablet ? '80px 32px 32px' : '32px 48px', minHeight: '100vh', width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box', background: colors.bg, animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)' };

  // Prominent, search-engine-style bar with generous whitespace, prefilled with the
  // current query. Keyed by query so a fresh search reseeds the field.
  const topBar = (
    <div style={{ display: 'flex', justifyContent: 'center', padding: isMobile ? '4px 0 28px' : '24px 0 48px' }}>
      <div style={{ width: '100%', maxWidth: isMobile ? '100%' : '640px' }}>
        <SearchBar key={query} variant="prominent" initialValue={query} placeholder="Search videos, instructors, topics..." />
      </div>
    </div>
  );

  // THE IMAGE PATH ARRIVES WITH NO ?q=. SearchBar navigates to /search carrying
  // an upload marker in location.state, so `query` is empty and this would have
  // swallowed the whole flow — empty state rendered, panel never mounted.
  //
  // The page is only genuinely empty when there is no query AND nothing is
  // being transcribed AND no problems have arrived.
  const nothingToShow = !query && !transcribing && !askProblems && !uploadError;

  if (nothingToShow) {
    return (
      <div style={pageStyle}>
        {topBar}
        <div style={{ textAlign: 'center', padding: '80px 20px', color: colors.muted, fontFamily: '"DM Sans", sans-serif' }}>
          <h1 style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: isMobile ? '28px' : '36px', color: colors.text, fontWeight: 400, margin: '0 0 8px' }}>Search the library</h1>
          <p style={{ margin: 0 }}>Type a query to find lessons by title, description, or tag.</p>
        </div>
        <MathProof colors={colors} isLight={isLight} />
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {topBar}
      <div key={query || 'upload'} style={{ animation: 'fadeIn 0.4s ease' }}>
      {/* ══ BLOCK ONE — the wait, then the panel ═══════════════════════════
          1.5's two timelines.

          TYPED: no orrery. `askProblems` is set synchronously from the query,
          so the panel mounts immediately with one pending card — what they
          typed, verbatim, with a loading bar. Then everything populates at
          once.

          IMAGE: the orrery ALONE while /search/image runs. `askProblems` is
          null so the panel renders nothing — there is no card to show, because
          nothing has been read yet. When the transcription lands the cards
          appear with loading bars in their answer slots, and the instructions
          follow only once an answer says which were used.

          TIER: the panel gates itself on PANEL_TIERS (pro_plus only), and the
          orrery and error box below are gated on the same list via
          `showPanel`. Free and pro see none of the three. */}
      {/* THE PANEL'S CONTAINER — the ONE place its width is set.
          Everything inside is fluid. 720 with 24px padding puts a card's outer
          edge at 640, which is what the search bar above uses, so the two line
          up without the card having to know the bar's width.

          On a phone maxWidth does nothing and the padding is all that holds it
          off the edges. */}
      <div style={{
        maxWidth: PANEL.maxWidth,
        margin: '0 auto',
        padding: isMobile ? `0 ${PANEL.padXMobile}px` : `0 ${PANEL.padX}px`,
        boxSizing: 'border-box',
      }}>
        {showPanel && transcribing && (
          <Orrery phrase={phrase} seconds={secondsLeft} isMobile={isMobile} />
        )}

        {/* The upload failed. Says why, in the server's own words, and the
            lexical results below still render — a failed photo costs the
            interpretation, not the page. PANEL_TIERS only, like the panel. */}
        {showPanel && uploadError && (
          <div style={{ background: PANEL.bg, borderRadius: 12, padding: '1rem', marginBottom: 16 }}>
            <div style={{
              background: PANEL.surface, border: `0.5px solid ${PANEL.border}`,
              borderRadius: 12, padding: '0.9rem 1.15rem',
              display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <span aria-hidden="true" style={{ color: PANEL.accentText, fontSize: 15, lineHeight: 1.5 }}>!</span>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6,
                color: PANEL.secondary, fontFamily: PANEL.sans }}>
                {uploadError}
              </p>
            </div>
          </div>
        )}

        {/* KEYED ON THE NAVIGATION. A new question REMOUNTS the panel, which
            is what actually clears it.
            
            Resetting inside the panel was not enough. That happens in an effect
            depending on `askProblems`, which the PAGE sets in its own effect —
            so the log did not clear until two render cycles after the URL
            changed, and the previous answer sat on screen for both of them.
            
            It also leaves refs behind. `activeLabel` is `question` for every
            typed search, so React reuses the same ProblemCard instance and
            `lastUsedRef` keeps the PREVIOUS question's instruction ids. A log
            reset cannot reach that; a remount can.
            
            `location.key` is unique per navigation entry and does NOT change on
            a resubmit — so a resubmit keeps the panel and its log, which is
            exactly right. */}
        <InterpretationPanel
          key={navKey}
          currentTier={tier}
          initialProblems={askProblems}
          sendTree={sendTree}
          sendExplain={sendExplain}
        />
      </div>

      {/* ══ BLOCK TWO — the results ═════════════════════════════════════════
          NOT HELD. The hold existed because enrichment reordered these rows two
          seconds after they appeared, and a student who had started reading
          lost their place. Enrichment is off for this build, the order never
          changes, so there is nothing to wait for — the results render
          immediately while the panel works above them. */}
      {/* The lexical area: headline, hero, tabs and the selected tab's results
          (LexicalResults). Only when there is a query — the image path has none.
          Topic rows stay the existing ResultRows, passed in unchanged. */}
      {!query ? null : (catalogLoading && !(catalog && catalog.length)) ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: colors.muted, fontFamily: '"DM Sans", sans-serif', fontSize: '14px' }}>
          Loading the library…
        </div>
      ) : (hasResults || hasHierarchy) ? (
        <LexicalResults
          key={query}
          query={query}
          courses={hier.courses}
          chapters={hier.chapters}
          sections={hier.sections}
          topicsCount={data.total}
          topicsContent={data.groups.map((g) => (
            <ResultRow key={g.key} title={g.label} videos={g.items} colors={colors} isLightTheme={isLightTheme}
              size={g.key === 'titles' ? 'large' : 'normal'} isMobile={isMobile} isTablet={isTablet} onNavigate={onNavigate} />
          ))}
          onOpenTopic={openTopic}
          isMobile={isMobile} isTablet={isTablet} isDesktop={isDesktop} sidebarOpen={sidebarOpen}
        />
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: colors.muted, fontFamily: '"DM Sans", sans-serif' }}>
          <h2 style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: isMobile ? '24px' : '30px', color: colors.text, fontWeight: 400, margin: '0 0 8px' }}>No videos match your search terms</h2>
          <p style={{ margin: 0 }}>Nothing in the library matches &ldquo;{query}&rdquo;. Try a different word or check the spelling.</p>
        </div>
      )}
      </div>

      <div style={{ height: isMobile ? '32px' : '48px' }} />

      <UpgradePrompt open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

      <style>{`
        .video-row-scroll::-webkit-scrollbar { height: 0; width: 0; display: none; }
        @keyframes srPulse { 0%,100% { opacity: 0.35; } 50% { opacity: 1; } }
        .sr-pulse { animation: srPulse 1.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .sr-pulse { animation: none; } }
        ${ANSWER_STYLES}
      `}</style>
    </div>
  );
}
